import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@shop/api-client'
import { formatMoney, toNumber } from '@shop/shared'
import { Banner, ErrorBanner, Spinner } from '@shop/ui'
import { Figure, Keypad, Sheet, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { CashSession } from '../pos/contracts'

/**
 * إغلاقُ الورديّة — <b>والعدُّ بيدِ الكاشير لا رقمٌ يُفترض</b>.
 *
 * ⚠ <b>والمتوقَّعُ يُعرض قبل العدّ عمدًا.</b> وإخفاؤه «حتى لا يتأثّر العادّ»
 *   فكرةٌ تبدو حصيفة، ونتيجتُها أنّ الكاشيرَ يعدّ ثمّ يُصدَم برقمٍ لا يعرف من
 *   أين جاء. <b>والشفافيّةُ هنا أنفعُ من الاختبار</b>: الفرقُ يُسجَّل بسببه
 *   على أيّ حال، ومن أراد الغشَّ يعدّ ما يشاء في الحالتين.
 *
 * <b>والبطاقةُ تُعرض ولا تدخل المتوقَّع</b> — لأنّ <c>affects_cash_drawer</c>
 * لها <c>false</c>، لا لأنّ الشاشةَ تعرف كلمةَ «بطاقة».
 */
export default function CloseShift({
  session,
  onClosed,
  onClose,
}: {
  session: CashSession
  onClosed: () => void
  onClose: () => void
}) {
  const { logout } = useAuth()
  const [counted, setCounted] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  /** الورديّةُ بعد إغلاقها — ما دامت هنا فالشاشةُ شاشةُ نتيجةٍ لا إدخال. */
  const [done, setDone] = useState<CashSession | null>(null)

  // ⚠ **والتقريرُ يُقرأ الآن لا من ذاكرة**: بيعةٌ وقعت قبل ثوانٍ تغيّر
  //   المتوقَّع، وإغلاقٌ على رقمٍ قديمٍ يُنتج فرقًا لا وجودَ له.
  const query = useQuery({
    queryKey: ['shift-report', session.id],
    queryFn: () => pos.shiftReport(session.id),
    staleTime: 0,
  })

  const report = query.data ?? null

  const countedValue = toNumber(counted)
  const expected = report?.expectedCashNow ?? 0
  const difference = countedValue === null ? null : round(countedValue - expected)

  const needsReason = difference !== null && difference !== 0
  const realReason = reason.replace(/\s/g, '').length >= 5

  const submit = async () => {
    if (countedValue === null) {
      return
    }

    setBusy(true)
    setError(null)

    try {
      // ⚠ **ولا يُغادَر فورًا.** من أغلق الدرجَ يريد أن يرى الفرقَ ثمّ يقرّر:
      //   يفتح ورديّةً جديدةً، أو يُسلّم الجهازَ لغيره فيخرج. والانتقالُ
      //   التلقائيُّ إلى شاشة «افتح ورديّة» يبتلع النتيجةَ قبل أن تُقرأ.
      setDone(await pos.closeShift(session.id, countedValue, needsReason ? reason.trim() : undefined))
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    const gap = done.difference ?? 0

    return (
      <Sheet title={`أُغلقت الورديّة ${done.sessionNo}`} onClose={onClosed}>
        <div className="mx-auto grid max-w-md gap-4">
          <Banner tone={gap === 0 ? 'ok' : 'warn'}>
            {gap === 0
              ? 'المعدودُ طابق المتوقَّع — لا فرق.'
              : `فرقُ الدرج ${gap > 0 ? '+' : ''}${gap.toFixed(3)} — وسُجّل بسببه باسمك.`}
          </Banner>

          <div className="grid grid-cols-3 gap-2">
            <Figure label="المتوقَّع" value={formatMoney(done.expectedCash ?? 0)} />
            <Figure label="المعدود" value={formatMoney(done.countedCash ?? 0)} />
            <Figure
              label="الفرق"
              value={formatMoney(gap)}
              tone={gap === 0 ? 'ok' : 'warn'}
            />
          </div>

          {/*
            ⚠ **وزرُّ الخروج هنا لأنّ هذه لحظتُه.** من أغلق الدرجَ إمّا يُكمل
              بورديّةٍ جديدة، وإمّا انتهى يومُه فيُسلّم الجهاز. وتركُه يبحث عن
              «خروج» في الترويسة بعد إغلاق الدرج عملٌ زائدٌ في أكثر اللحظات
              استعجالًا.
          */}
          <TouchButton wide tone="primary" onClick={onClosed}>
            افتح ورديّةً جديدة
          </TouchButton>

          <button
            className="min-h-14 rounded-lg border border-line text-base text-ink-soft"
            type="button"
            onClick={() => void logout()}
          >
            خروجٌ من الحساب
          </button>
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet title={`إغلاقُ الورديّة ${session.sessionNo}`} onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-4">
        {query.isPending && <Spinner />}

        {report && (
          <>
            {/* ⚠ **قيمةُ المبيعات غيرُ النقد الداخل** — وكانت الشاشةُ تعرض
                عددَ البيعات وحدَه بالمال، فبيعةٌ بالبطاقة لا تظهر في رقمٍ
                واحدٍ منها. والرقمان يُعرضان معًا ولا يُستبدل أحدُهما بالآخر.
                وكلُّها **للورديّة وحدَها** لا لليوم. */}
            <div className="grid grid-cols-2 gap-2">
              <Figure label="مبيعاتُ الورديّة" value={formatMoney(report.salesTotal)} />
              <Figure label="عددُ البيعات" value={String(report.salesCount)} />
              {report.returnsTotal > 0 && (
                <>
                  <Figure label="مرتجعاتُ الورديّة" value={formatMoney(report.returnsTotal)} />
                  <Figure label="صافي المبيعات" value={formatMoney(report.netSales)} />
                </>
              )}
              <Figure label="الافتتاحيّ" value={formatMoney(report.session.openingCash)} />
              <Figure label="نقدٌ دخل" value={formatMoney(report.cashIn)} />
              <Figure label="نقدٌ خرج" value={formatMoney(report.cashOut)} />
              {report.payouts > 0 && (
                <Figure label="مصاريفُ من الدرج" value={formatMoney(report.payouts)} tone="warn" />
              )}
            </div>

            <Figure label="النقدُ المتوقَّع في الدرج" value={formatMoney(expected)} />

            {report.nonCashIn > 0 && (
              <Banner tone="info">
                بطاقةٌ وغيرُها: {formatMoney(report.nonCashIn)} —{' '}
                <b>تُعرض في التقرير ولا تدخل الدرج</b>.
              </Banner>
            )}

            <label className="block">
              <span className="mb-1 block text-xs text-ink-soft">ما عددتَه في الدرج</span>
              <input
                className="tabular min-h-14 w-full rounded-xl border-2 border-line bg-surface px-3 text-xl"
                dir="ltr"
                inputMode="decimal"
                value={counted}
                onChange={(event) => setCounted(event.target.value)}
              />
            </label>

            <Keypad value={counted} onChange={setCounted} />

            {difference !== null && (
              <Figure
                label="الفرق"
                value={formatMoney(difference)}
                tone={difference === 0 ? 'ok' : 'warn'}
              />
            )}

            {needsReason && (
              <label className="block">
                <span className="mb-1 block text-xs text-ink-soft">سببُ الفرق</span>
                <input
                  className="min-h-12 w-full rounded-lg border border-line bg-surface px-3"
                  value={reason}
                  placeholder="خمسةُ محارفَ فأكثر"
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
            )}

            <ErrorBanner error={error} />

            <TouchButton
              tone="primary"
              wide
              disabled={busy || countedValue === null || (needsReason && !realReason)}
              onClick={() => void submit()}
            >
              {busy ? 'يُغلق…' : 'أغلِق الورديّة'}
            </TouchButton>
          </>
        )}

        <ErrorBanner error={query.error} />
      </div>
    </Sheet>
  )
}

function round(value: number) {
  return Math.round(value * 1000) / 1000
}
